import { useState, type ReactNode } from 'react';
import { api, ApiError, type CaringEntry, type Channel, type InviteAccepted, type InvitePreview } from '../api.ts';

/**
 * The Approve / Reject prompt for an invitation.
 *
 * Shaped like a sign-in security prompt on purpose: one question, who is
 * asking and from which number, exactly what is shared, and two buttons.
 * An invitation grants someone a view of another person's medicines and a
 * place on their alert ladder, so the answer has to be an informed one —
 * the thin "X invited you · Accept" card made it a reflex.
 *
 * Used twice, so the two cannot drift: the /i/:token page a link opens, and
 * the pending-invitation cards on the signed-in home screen.
 *
 * Everything shown comes from `GET /invites/:token`, and the server re-derives
 * it on accept rather than trusting what this screen says it showed.
 */

const ROLE_NAME: Record<string, string> = {
  caregiver: 'Caregiver', guardian: 'Guardian', viewer: 'Viewer',
  staff: 'Staff', clinician: 'Clinician', self: 'Their own phone',
};

const ROLE_DETAIL: Record<string, string> = {
  caregiver: 'Can mark doses as taken.',
  guardian: 'Can mark doses as taken, and add and change medicines.',
  viewer: 'Can see, but not change anything.',
  staff: 'As a member of their care home’s staff.',
  clinician: 'As one of their doctors.',
};

/*
 * No box for voice, deliberately. And because the accept call sends only the
 * ticked boxes, a voice channel on offer is dropped rather than kept: nobody
 * is recorded as agreeing to a channel this screen never showed them.
 */
const CHANNEL_LABEL: Partial<Record<Channel, string>> = {
  push: 'App notification',
  whatsapp: 'WhatsApp from DrBell',
  sms: 'SMS from DrBell',
  email: 'Email from DrBell',
};

const PLAN_NAME: Record<InvitePreview['tier'], string> = {
  free: 'Free', plus: 'Plus', family: 'Family', org: 'care home',
};

/** What went wrong with an answer, in terms this screen can act on. */
export type AnswerFailure =
  | { kind: 'ended' }
  | { kind: 'phone_mismatch' }
  | { kind: 'no_channel' }
  | { kind: 'other'; message: string };

export function failureOf(e: unknown): AnswerFailure {
  if (e instanceof ApiError) {
    if (e.status === 404 || e.status === 410) return { kind: 'ended' };
    if (e.status === 403) return { kind: 'phone_mismatch' };
    if (e.status === 400 && /no_channel|at least one way/i.test(e.message)) return { kind: 'no_channel' };
    return { kind: 'other', message: e.message };
  }
  return { kind: 'other', message: e instanceof Error ? e.message : String(e) };
}

/**
 * Approve, keeping these channels. Self invitations take none.
 *
 * `ringsHere` is what the card said about the reminders ringing on their
 * phone: the server refuses the answer (409) if the invitation changed
 * since, rather than record agreement to terms nobody was shown.
 */
export function approveInvite(token: string, channels: Channel[] | undefined, ringsHere?: boolean) {
  return api.post<InviteAccepted>(`/api/invites/${token}/accept`, {
    ...(channels ? { channels } : {}),
    ...(ringsHere !== undefined ? { ringsHere } : {}),
  });
}

export function rejectInvite(token: string) {
  return api.post<{ ok: boolean }>(`/api/invites/${token}/decline`);
}

/** The one name every sentence uses for the person who asked. */
export function inviterOf(preview: InvitePreview): string {
  return preview.inviterName ?? preview.invitedBy ?? 'Someone';
}

export function patientOf(preview: InvitePreview): string {
  return preview.patientName || 'someone';
}

export function dateOf(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

interface Props {
  token: string;
  preview: InvitePreview;
  /**
   * Answer the invitation. Absent when signed out: the details are still
   * shown — they are what the person needs to decide whether to sign in at
   * all — and `signIn` takes the buttons' place.
   */
  onApprove?: (channels: Channel[] | undefined) => void;
  onReject?: () => void;
  signIn?: ReactNode;
  busy?: boolean;
  /** Shown above the buttons: the server refused the last answer. */
  problem?: ReactNode;
  /** The app link: on the page a link opened, not on the home screen. */
  showAppLink?: boolean;
}

export function InviteCard({ token, preview, onApprove, onReject, signIn, busy, problem, showAppLink }: Props) {
  const inviter = inviterOf(preview);
  const patient = patientOf(preview);
  const isSelf = preview.role === 'self';

  // On Free a caregiver is told nothing (owner, 20 Oct 2026): no alerts or ways to list.
  const freeQuiet = preview.caregiverAlerts === false;
  const offered = freeQuiet ? [] : preview.channels.filter((c) => CHANNEL_LABEL[c]);
  // Every offered channel starts on: the inviter chose them, and a box the
  // invitee has to find and tick is a box most people never tick.
  const [kept, setKept] = useState<Channel[]>(offered);
  const noneKept = !isSelf && offered.length > 0 && kept.length === 0;

  const told = [
    preview.alerts.missed && 'A dose is missed',
    preview.alerts.late && 'A dose is late',
    preview.alerts.taken && 'A dose is taken',
  ].filter((x): x is string => Boolean(x));

  return (
    <article className="rounded-2xl border border-(--accent) bg-(--bg) p-5 sm:p-6">
      <h2 className="text-xl font-semibold tracking-tight">
        {isSelf ? `${inviter} set up DrBell for you` : `${inviter} asks you to help look after ${patient}`}
      </h2>
      {preview.inviterPhone && (
        <p className="mt-1 text-sm text-(--muted)">
          From {inviter}, <span className="whitespace-nowrap font-mono">{preview.inviterPhone}</span>
        </p>
      )}

      <div className="mt-5 space-y-4 border-t border-(--line) pt-4 text-sm">
        {isSelf ? (
          <>
            <p>
              Approve to get your medicine reminders on this phone. {inviter} will still see your
              medicines and whether you took each dose, can change them, and is told if you miss one.
            </p>
            <p className="text-(--muted)">
              If you reject, {inviter} keeps the record and this phone is not linked.
            </p>
          </>
        ) : (
          <>
            <div>
              <p className="font-medium">As {ROLE_NAME[preview.role] ?? preview.role}</p>
              <p className="text-(--muted)">
                You will see {patient}&rsquo;s medicines and whether each dose was taken.
                {ROLE_DETAIL[preview.role] ? ` ${ROLE_DETAIL[preview.role]}` : ''}
              </p>
            </div>

            {/* A hands-on helper: an alarm on their own phone is a term like the rest, said plainly. */}
            {preview.ringsHere && (
              <p className="font-medium">
                Reminders for {patient} will ring on your phone, and you can mark doses as taken.
              </p>
            )}

            {freeQuiet ? (
              <p className="font-medium">
                On {preview.inviterName ? `${preview.inviterName}’s` : 'this'} Free plan you can see the medicines and
                each dose, but you are not told about them.
              </p>
            ) : (
            <div>
              <p className="font-medium">You will be told when</p>
              {told.length > 0 ? (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-(--muted)">
                  {told.map((t) => <li key={t}>{t}</li>)}
                </ul>
              ) : (
                <p className="text-(--muted)">Nothing: you will not be sent alerts.</p>
              )}
            </div>
            )}

            {offered.length > 0 && (
              <fieldset>
                <legend className="font-medium">How you will be told</legend>
                <div className="mt-2 space-y-1">
                  {offered.map((c) => (
                    <label key={c} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-(--line) px-3">
                      <input
                        type="checkbox"
                        checked={kept.includes(c)}
                        disabled={!onApprove || busy}
                        onChange={(e) =>
                          setKept((now) => (e.target.checked ? [...now, c] : now.filter((x) => x !== c)))
                        }
                        className="h-4 w-4"
                      />
                      {CHANNEL_LABEL[c]}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-(--muted)">
                  Turn off any you don&rsquo;t want. You can change this later.
                </p>
              </fieldset>
            )}
          </>
        )}

        <dl className="space-y-1 text-(--muted)">
          <div>
            <dt className="sr-only">Plan</dt>
            <dd>
              {preview.inviterName
                ? `On ${preview.inviterName}’s ${PLAN_NAME[preview.tier] ?? preview.tier} plan`
                : `On a ${PLAN_NAME[preview.tier] ?? preview.tier} plan`}
            </dd>
          </div>
          {preview.expiresAt && (
            <div>
              <dt className="sr-only">Ends</dt>
              <dd>This invitation ends on {dateOf(preview.expiresAt)}</dd>
            </div>
          )}
        </dl>

        {/* The one sentence a scam would have to talk somebody past. A link
            like this arrives from a family WhatsApp; the dangerous version is
            a stranger on the phone saying "just press approve". */}
        <p className="rounded-lg border border-(--brand) bg-(--brand-soft) px-3 py-2">
          Only approve if you know {preview.inviterName ?? 'the person who sent this'}. DrBell never asks
          you to approve anything on a phone call.
        </p>
      </div>

      {noneKept && (
        <p role="alert" className="mt-4 text-sm text-red-500">Keep at least one way to be told.</p>
      )}
      {problem && <div className="mt-4">{problem}</div>}

      <div className="mt-5">
        {onApprove && onReject ? (
          <div className="flex flex-wrap justify-end gap-2">
            <button
              disabled={busy}
              onClick={onReject}
              className="min-h-11 rounded-lg border border-(--line) px-5 text-sm hover:border-(--accent) disabled:opacity-40"
            >
              Reject
            </button>
            <button
              disabled={busy || noneKept}
              onClick={() => onApprove(isSelf || offered.length === 0 ? undefined : kept)}
              className="min-h-11 rounded-lg bg-(--accent) px-5 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Saving…' : 'Approve'}
            </button>
          </div>
        ) : (
          signIn
        )}
      </div>

      {showAppLink && (
        <p className="mt-4 text-center text-xs">
          <a href={`drbell://invite/${encodeURIComponent(token)}`} className="text-(--muted) underline hover:text-(--fg)">
            Open in the DrBell app
          </a>
        </p>
      )}
    </article>
  );
}

/*
 * "Ring {patient}'s reminders on your phone?" — the family's question to a
 * helper already on the record (ringing needs both sides, the owner's
 * decision of 3 Oct 2026). Drawn like the invitation that would have asked
 * it, with the same sentence in bold, because it is the same agreement:
 * an alarm on their own phone. Shown in the Invitations area and in How
 * I'm told. Only Accept makes it ring; nothing is asked of a phone until
 * then.
 */

/** Answer it. 404 when it lapsed, was withdrawn or was answered already. */
export function answerRingingRequest(grantId: string, accept: boolean) {
  return api.post<{ ok: boolean; ringsHere: boolean }>(`/api/me/caring/${grantId}/ringing/${accept ? 'accept' : 'decline'}`);
}

export function ringingRequestTitle(entry: Pick<CaringEntry, 'patientName' | 'ringingRequest'>): string {
  const name = entry.ringingRequest?.askerName;
  return name
    ? `${name} asks for ${entry.patientName}’s reminders to ring on your phone`
    : `${entry.patientName}’s family asks for the reminders to ring on your phone`;
}

export function RingingRequestCard({
  entry, busy, onAnswer, compact,
}: {
  entry: CaringEntry;
  busy?: boolean;
  onAnswer: (accept: boolean) => void;
  /** Inside How I'm told's card: a smaller heading, no outer margin. */
  compact?: boolean;
}) {
  const patient = entry.patientName;
  return (
    <article className={`rounded-2xl border border-(--accent) bg-(--bg) ${compact ? 'p-4' : 'p-5 sm:p-6'}`}>
      <h2 className={compact ? 'text-base font-semibold' : 'text-xl font-semibold tracking-tight'}>{ringingRequestTitle(entry)}</h2>
      <div className="mt-4 space-y-3 border-t border-(--line) pt-4 text-sm">
        <p className="font-medium">Reminders for {patient} will ring on your phone, and you can mark doses as taken.</p>
        <p className="text-(--muted)">
          For whoever gives the medicines. Taken and Later work from the alarm, and the family is still told
          about a missed dose.
        </p>
        {entry.ringingRequest && (
          <p className="text-(--muted)">This request ends on {dateOf(entry.ringingRequest.expiresAt)}.</p>
        )}
      </div>
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <button
          disabled={busy}
          onClick={() => onAnswer(false)}
          className="min-h-11 rounded-lg border border-(--line) px-5 text-sm hover:border-(--accent) disabled:opacity-40"
        >
          Decline
        </button>
        <button
          disabled={busy}
          onClick={() => onAnswer(true)}
          className="min-h-11 rounded-lg bg-(--accent) px-5 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Accept'}
        </button>
      </div>
    </article>
  );
}
